import React, {
  ReactElement,
  useState,
  SyntheticEvent,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import { Box, Text } from 'rebass/styled-components';
import { Tooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';
import useOnClickOutside from 'use-onclickoutside';

import {
  FileBrowserBtn,
  BrokenLinkCount,
  ProcedureSearch,
  ProcedureList,
  ProcedureListItem,
  ProcedureBrokenLinks,
  FilterMenu,
  FilterMenuItem,
  FilterMenuItemLabel,
  FilterMenuActions,
} from '../styles';
import LinkedAccountsList from '../LinkedAccountsList';
import LinkedGlobalAccountsList from '../LinkedGlobalAccountsList';

import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Dropdown,
  DropdownButton,
  DropdownMenu,
  StyledDropdown,
} from 'ui/patterns';
import { Button, Checkbox, Stack } from 'ui/components';

import { useDocumentTitle, useGetBrokenLinksForDismissal } from 'hooks';

import * as Icon from 'assets/icons';
import NoDataImage from 'assets/images/NoDataImage';

import { showSuccessToast, showErrorToast } from 'utils/showToast';
import { confirmAlert } from 'utils/confirm-alert';

import {
  ProcedureBuilderListType,
  ProcedureReturnType,
} from 'types/procedureBuilder';
import {
  ProceduresListPropType,
  ProcedureItemType,
} from './procedureListTypes';
import { ErrorModal } from 'types/modalTypes';

export default function ProceduresLists({
  accountId,
  totalBrokenLinksCount,
  isProcedureLoading,
  procedures,
  selectedProcedures,
  setSelectedProcedures,
  updateProcedureVisibility,
  renameProc,
  setRenameProc,
  handleRenameProcedures,
  searchValue,
  setSearchValue,
  selectProcedure,
  appliedFilterOption,
  setAppliedFilterOption,
  addProcedure,
  importProcedure,
  linkProcedure,
  toggleFileBrowser,
  setRenameFailed,
  renameFailed,
  brokenLinks,
  autoSaveContent,
  procedureDetails,
  setUserTyped,
  setEditorChanges,
  saveContent,
  userTyped,
  editorChanges,
  procedureCategories,
}: ProceduresListPropType): ReactElement {
  useDocumentTitle('Procedure Builder', true);

  //Handling global and linked account modals in the procedure
  const [globalProcedure, setGlobalProcedure] =
    useState<ProcedureBuilderListType>();

  const [linkedProcedure, setLinkedProcedure] =
    useState<ProcedureBuilderListType>();

  const [filterOption, setFilterOption] = useState<string>('');
  const [open, setOpen] = useState(false);

  const { refetch: refetchBrokenLinks, isFetching: isBrokenLinksLoading } =
    useGetBrokenLinksForDismissal({
      accountId,
      showPanel: true,
      payloadString: '',
    });

  const toggleLinkedGlobalAccounts = (
    procedureItem?: ProcedureBuilderListType,
  ) => {
    setGlobalProcedure(procedureItem);
  };
  const toggleLinkedAccounts = (procedureItem?: ProcedureBuilderListType) => {
    setLinkedProcedure(procedureItem);
  };

  const handleFileBrowser = () => {
    toggleFileBrowser();
  };

  useEffect(() => {
    setFilterOption(appliedFilterOption);
  }, [appliedFilterOption]);

  const linkProcedureHandler = () => {
    linkProcedure();
  };

  const procedureAddOptions = [
    {
      value: 'Add Procedure',
      icon: <Icon.DocumentAdd title="" ariaHidden="true" />,
      groupEnd: true,
      action: () => {
        addProcedure();
      },
    },
    {
      value: 'Import Procedure',
      icon: <Icon.Import title="" ariaHidden="true" />,
      groupEnd: true,
      action: () => {
        importProcedure();
      },
    },
    {
      value: 'Link Procedure',
      icon: <Icon.DocumentLink title="" ariaHidden="true" />,
      action: () => {
        linkProcedureHandler();
      },
    },
  ];

  const procedureAddDropdown = () => (
    <Button size="sm" variant="subtle">
      <Icon.DotsVertical title="Options" />
    </Button>
  );

  const procedureFilterOptions = [
    {
      id: 1,
      value: 'Procedures with broken links',
      icon: <Icon.Link />,
    },
    {
      id: 2,
      value: 'Un-published procedures',
      icon: <Icon.ExternalLink />,
    },
    {
      id: 3,
      value: 'Linked procedures',
      icon: <Icon.DocumentLink />,
    },
    {
      id: 4,
      value: 'Global procedures',
      icon: <Icon.Globe />,
    },
    {
      id: 5,
      value: 'Visible procedures',
      icon: <Icon.Eye />,
    },
    {
      id: 6,
      value: 'Invisible procedures',
      icon: <Icon.EyeOff />,
    },
  ];

  const isProcedureChecked = (id: number) => {
    const item =
      selectedProcedures && selectedProcedures.find(item => item === id);
    if (item) {
      return true;
    } else return false;
  };

  const isCheckedAllProcedures = () => {
    if (
      selectedProcedures?.length ===
      (procedures && procedures.length > 0 && procedures.length)
    )
      return true;
    else return false;
  };

  const selectAllProcedureAction = () => {
    setRenameProc(undefined);
    let allprocedures: Array<number> = [];
    const isSelect = isCheckedAllProcedures();
    if (!isSelect) {
      allprocedures =
        procedures && procedures.length > 0
          ? procedures.map(item => {
              return item.ProcedureId;
            })
          : [];
    } else {
      allprocedures = [];
    }
    if (allprocedures) setSelectedProcedures([...allprocedures]);
  };

  const handleSelectAllProcedures = () => {
    let newContent = '';
    if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
      newContent = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
        procedureDetails?.AccountProcedure?.AccountId
      }/Policies/'></head><body>${(
        window as any
      )?.tinymce?.activeEditor?.getContent()}</body></html>`;
    }
    if (
      (window as any)?.tinymce?.activeEditor?.isDirty() ||
      (userTyped === true && newContent !== procedureDetails?.Content)
    ) {
      confirmAlert({
        message: 'Unsaved changes are present do you want to save the changes?',
        buttons: [
          {
            label: 'Yes',
            onClick: () => {
              saveContent(() => {
                setUserTyped(false);
                setEditorChanges(false);
              });
              selectAllProcedureAction();
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'No',
            onClick: () => {
              //
            },
          },
        ],
      });
    } else {
      selectAllProcedureAction();
    }
  };

  /*********SINGLE PROCEDURE VISIBILITY TOGGLE***************************/
  //Do not show again checkbox//
  const handleSingleToggleModalVisiblity = (e: any) => {
    if (e.target.checked === true) {
      localStorage.setItem(
        'singleVisibilityModalConfirmation',
        JSON.stringify(true),
      );
    } else {
      localStorage.removeItem('singleVisibilityModalConfirmation');
    }
  };

  //Function to handle apis
  const changeProcedureVisibility = (procItem: any) => {
    updateProcedureVisibility(
      {
        accountId: procItem.AccountId,
        procedureIds: [procItem.ProcedureId],
        visibilityStatus: !procItem.IsVisible ? 1 : 0,
      },
      {
        onSuccess: () => {
          showSuccessToast({
            message: 'Your changes were saved successfully.',
          });
          setSearchValue('');
        },
        onError: (error: ErrorModal) => {
          showErrorToast({
            message: error.Errors[0].Message,
          });
        },
      },
    );
  };

  const confirmVisibilityToggle = (
    procItem: any,
    cb: (isSuccess: boolean) => void,
  ) => {
    setTimeout(() => {
      if (procItem.IsLinked || procItem?.IsGlobal) {
        confirmAlert({
          message:
            'You are about to change the visibility of a global and/or linked procedure. This change would affect all the other accounts to which it has been added. Do you want to proceed?',
          buttons: [
            {
              label: 'Yes',
              onClick: () => {
                changeProcedureVisibility(procItem);
              },
              className: 'pr-btn-primary',
            },
            {
              label: 'No',
              onClick: () => {
                cb(false);
                //
              },
            },
          ],
          onClickOutside: () => cb(false),
          onKeypressEscape: () => cb(false),
        });
      } else {
        changeProcedureVisibility(procItem);
      }
    }, 10);
  };

  function hideDisplayProcedure(
    procItem: any,
    cb: (isSuccess: boolean) => void,
  ) {
    const tempValue = localStorage.getItem('singleVisibilityModalConfirmation');
    const showConfirmation = tempValue === null ? false : JSON.parse(tempValue);
    const isFirstTimeProcedure = procItem?.IsDraft;
    if (!showConfirmation) {
      confirmAlert({
        dontShowAgain: isFirstTimeProcedure ? false : true,
        handleModalVisibility: handleSingleToggleModalVisiblity,
        message: isFirstTimeProcedure
          ? 'One or more of the selected procedures have never been published and are not yet available to users. In order to add them to the Policies Menu, all such procedures will also be published. Proceed ? '
          : `The procedure will be ${
              procItem.IsVisible === true
                ? 'removed from the'
                : 'displayed in the '
            }  Policies Menu,  ${
              procItem.IsVisible === true
                ? 'but procedures will still be able to link to it.'
                : ''
            }`,
        buttons: [
          {
            label: 'Ok',
            onClick: () => {
              confirmVisibilityToggle(procItem, cb);
            },
            className: 'pr-btn-primary',
          },
          {
            label: 'Cancel',
            onClick: () => {
              cb(false);
              //
            },
          },
        ],
        onClickOutside: () => cb(false),
        onKeypressEscape: () => cb(false),
      });
    } else {
      confirmVisibilityToggle(procItem, cb);
    }
  }
  /*********SINGLE PROCEDURE VISIBILITY TOGGLE**************************/

  const handleBrokenLinkChecker = () => {
    refetchBrokenLinks();
  };

  const handleOnClickOutside = () => {
    setOpen(false);
  };
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOnClickOutside(wrapperRef, handleOnClickOutside);
  const handleToggle = () => {
    setOpen(true);
  };

  return (
    <>
      <Card width="380px" flexDirection="column" style={{ display: 'flex' }}>
        <CardHeader
          py={1}
          px={3}
          bg="var(--color-neutral-contrast-01)"
          alignItems="center"
          style={{ display: 'flex' }}
        >
          <Text as="h4" fontWeight="medium" lineHeight={1} color="text.body">
            Procedures
          </Text>
          <Tooltip
            animation="fade"
            title="Broken links in this account"
            position="top"
            arrow={true}
          >
            <BrokenLinkCount fetching={isBrokenLinksLoading}>
              <Icon.Link title="Broken links " />
              <Text>{totalBrokenLinksCount}</Text>
              <button onClick={handleBrokenLinkChecker}>
                <Icon.Refresh />
              </button>
            </BrokenLinkCount>
          </Tooltip>
          <Box ml="auto" mr="-4px">
            <Dropdown
              trigger={procedureAddDropdown()}
              menuList={procedureAddOptions}
              position="left"
              className="dd-listitem-options"
            />
          </Box>
        </CardHeader>
        <ProcedureSearch>
          <Checkbox
            label=""
            name="selectAll"
            checked={isCheckedAllProcedures()}
            handleChange={handleSelectAllProcedures}
          />
          <div className="search-input">
            <Icon.Search />
            <input
              type="search"
              placeholder="Search procedures / code"
              value={searchValue}
              onChange={(e: SyntheticEvent<HTMLInputElement>) =>
                setSearchValue(e.currentTarget.value)
              }
            />
          </div>
          <Box ml="auto" mr="-4px">
            <StyledDropdown
              ref={wrapperRef}
              onClick={handleToggle}
              className="procedure-filter-dropdown"
            >
              <DropdownButton>
                <Icon.Filter
                  className={filterOption !== '' ? 'filtered' : ''}
                  title="Filter "
                />
              </DropdownButton>
              {open === true && (
                <DropdownMenu>
                  <FilterMenu>
                    {procedureFilterOptions.map(option => (
                      <FilterMenuItem
                        key={option.id}
                        selected={option.value === filterOption ? true : false}
                        onClick={() => setFilterOption(option.value)}
                      >
                        {option.icon}
                        <FilterMenuItemLabel>
                          {option.value}
                        </FilterMenuItemLabel>
                        {option.value === filterOption ? <Icon.Check /> : null}
                      </FilterMenuItem>
                    ))}
                  </FilterMenu>
                  <FilterMenuActions>
                    <button
                      onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setFilterOption('');
                        setOpen(false);
                        setAppliedFilterOption('');
                      }}
                    >
                      Clear filter
                    </button>
                    <button
                      onClick={(e: SyntheticEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        setAppliedFilterOption(filterOption);
                        setOpen(false);
                      }}
                      style={{ fontWeight: 500, color: 'var(--primary-color)' }}
                    >
                      Apply
                    </button>
                  </FilterMenuActions>
                </DropdownMenu>
              )}
            </StyledDropdown>
          </Box>
        </ProcedureSearch>
        <CardBody flex="1 0 auto" overflowY="auto" height="1px">
          <ProcedureList>
            {procedures &&
              procedures.length > 0 &&
              procedures?.map(procedureItem => {
                const isSelected = isProcedureChecked(
                  procedureItem.ProcedureId,
                );
                return (
                  <ProcedureItem
                    brokenLinks={brokenLinks}
                    setRenameFailed={setRenameFailed}
                    renameFailed={renameFailed}
                    key={procedureItem.ProcedureId}
                    procedureItem={procedureItem}
                    renameProc={renameProc}
                    isSelected={isSelected}
                    selectProcedure={selectProcedure}
                    changeVisibility={(cb: (isSucess: boolean) => void) => {
                      hideDisplayProcedure(procedureItem, cb);
                    }}
                    setRenameProc={(procedureItem?: ProcedureReturnType) => {
                      setRenameProc(procedureItem);
                    }}
                    updateName={handleRenameProcedures}
                    toggleLinkedGlobalAccounts={() =>
                      toggleLinkedGlobalAccounts(procedureItem)
                    }
                    toggleLinkedAccounts={() =>
                      toggleLinkedAccounts(procedureItem)
                    }
                    selectedProcedures={selectedProcedures}
                    isCheckedAllProcedures={isCheckedAllProcedures}
                    autoSaveContent={autoSaveContent}
                    procedureDetails={procedureDetails}
                    setUserTyped={setUserTyped}
                    setEditorChanges={setEditorChanges}
                    procedures={procedures}
                    editorChanges={editorChanges}
                    procedureCategories={procedureCategories}
                  />
                );
              })}
          </ProcedureList>
          {!isProcedureLoading && procedures && procedures.length <= 0 && (
            <NoDataImage height="100%" feedback="No data available" />
          )}
        </CardBody>
        <CardFooter backgroundColor="transparent">
          <FileBrowserBtn onClick={handleFileBrowser}>
            <Icon.Folder />
            <span className="label">File Browser</span>
            <Icon.ChevronRight
              title=""
              ariaHidden="true"
              fill="var(--color-neutral-contrast-05)"
            />
          </FileBrowserBtn>
        </CardFooter>
      </Card>

      <LinkedGlobalAccountsList
        showLinkedAccounts={!!globalProcedure}
        toggleLinkedAccounts={toggleLinkedGlobalAccounts}
        globalProcedure={globalProcedure}
      />
      <LinkedAccountsList
        showLinkedAccounts={!!linkedProcedure}
        toggleLinkedAccounts={toggleLinkedAccounts}
        linkedProcedure={linkedProcedure}
      />
    </>
  );
}

function ProcedureItem({
  procedureItem,
  renameProc,
  isSelected,
  selectProcedure,
  changeVisibility,
  setRenameProc,
  updateName,
  toggleLinkedGlobalAccounts,
  toggleLinkedAccounts,
  selectedProcedures,
  isCheckedAllProcedures,
  setRenameFailed,
  renameFailed,
  brokenLinks,
  autoSaveContent,
  procedureDetails,
  setUserTyped,
  setEditorChanges,
  editorChanges,
  procedureCategories,
}: ProcedureItemType) {
  const [newName, setNewName] = useState<string>();
  const [visibility, setVisibility] = useState<boolean>();
  const [isNameEdited, setIsNameEdited] = useState(false);
  const [editedProcedure, setEditedProcedure] =
    useState<ProcedureBuilderListType>();
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const procedureNameRef = useRef<string>();
  const procedureVisibilityRef = useRef<boolean>();

  const procedureNameInputRef = useRef<HTMLInputElement>(
    document.createElement('input'),
  );

  const procedureCategoryIcon = useMemo(() => {
    const category = procedureCategories?.find(
      category => category?.Id === procedureItem?.ProcedureCategoryId,
    );

    const defaultSvgString = `<svg width="24" height="24" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="12"  style="fill: var(--color-neutral-contrast-05)" />
  </svg>`;

    const svg = category?.Pattern || defaultSvgString;
    const hexCode = category?.HexCode || '';

    return `<span class="pcs-procedure-category__pattern_alias" style="--category-base: ${hexCode}">
    ${svg}
  </span>`;
  }, [procedureCategories, procedureItem?.ProcedureCategoryId]);

  const editMode = useMemo(() => {
    return renameProc?.ProcedureId === procedureItem.ProcedureId;
  }, [renameProc, procedureItem]);

  const isFirstTimeProcedure = procedureItem.IsDraft;

  useEffect(() => {
    if (editMode) procedureNameInputRef?.current.focus();
  }, [editMode, procedureNameInputRef]);

  useEffect(() => {
    if (
      !procedureNameRef.current ||
      procedureNameRef.current !== procedureItem?.ProcedureName
    ) {
      setNewName(procedureItem.ProcedureName);
      procedureNameRef.current = procedureItem?.ProcedureName;
    }
  }, [procedureItem]);

  useEffect(() => {
    if (
      !procedureVisibilityRef.current ||
      procedureVisibilityRef.current !== procedureItem?.IsVisible
    ) {
      setVisibility(procedureItem.IsVisible);
      procedureVisibilityRef.current = procedureItem?.IsVisible;
    }
  }, [procedureItem]);

  useEffect(() => {
    if (isNameEdited) {
      setNewName(editedProcedure?.ProcedureName);
    }
  }, [selectedProcedures, isNameEdited, editedProcedure]);

  useEffect(() => {
    if (renameFailed) {
      setNewName(procedureItem?.ProcedureName);
    }
  }, [procedureItem?.ProcedureName, renameFailed]);

  useEffect(() => {
    if (!editMode) {
      setTooltipOpen(false);
    }
  }, [editMode]);

  return (
    <ProcedureListItem
      unpublished={procedureItem.HasSavedChangesThatAreNotPublished}
      editMode={editMode}
      isSelected={isSelected}
      draft={isFirstTimeProcedure}
    >
      <Checkbox
        label=""
        name={`${procedureItem?.ProcedureName}${procedureItem.ProcedureId}`}
        checked={isSelected}
        handleChange={() => {
          setRenameProc(undefined);
          selectProcedure(true, procedureItem);
        }}
      />
      {/* if hidden procedure give eye off icon */}
      {newName === '0000_POLICIESMENU.html' ? (
        <Tooltip
          arrow={true}
          position="top"
          title="The Policy Menu cannot display a copy of itself."
          style={{ width: '20px', height: '20px' }}
        >
          <button
            style={{
              cursor: 'default',
            }}
            className="procedure__visibility"
            disabled
          >
            <Icon.EyeOff fill="var(--color-neutral-contrast-05)" />
          </button>
        </Tooltip>
      ) : (
        <button
          className="procedure__visibility"
          onClick={() => {
            let newContent = '';
            if ((window as any)?.tinymce?.activeEditor?.initialized === true) {
              newContent = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
                procedureDetails?.AccountProcedure?.AccountId
              }/Policies/'></head><body>${(
                window as any
              )?.tinymce?.activeEditor?.getContent()}</body></html>`;
            }
            setRenameProc(undefined);
            setEditorChanges(false);
            if (
              (window as any)?.tinymce?.activeEditor?.isDirty() ||
              newContent !== procedureDetails?.Content
            ) {
              autoSaveContent(() => {
                setUserTyped(false); ////check later
              });
            }
            const condition =
              selectedProcedures &&
              selectedProcedures.find(
                (item: any) => item !== procedureItem.ProcedureId,
              );

            if (condition) {
              selectProcedure(false, procedureItem);
            }
            changeVisibility((isSuccess: any) => {
              if (!isSuccess) {
                setVisibility(procedureVisibilityRef.current);
              }
            });
            setVisibility(!visibility);
          }}
        >
          {visibility === true ? (
            <Icon.Eye fill="var(--color-body-text)" />
          ) : (
            <Icon.EyeOff fill="var(--color-neutral-contrast-05)" />
          )}
        </button>
      )}
      {newName !== '0000_POLICIESMENU.html' && (
        <div
          title={
            procedureCategories?.find(
              category => category?.Id === procedureItem?.ProcedureCategoryId,
            )?.Label || 'Uncategorized'
          }
          style={{
            borderRadius: '50%',
            width: '24px',
            height: '24px',
            flexShrink: 0,
            marginLeft: '10px',
          }}
          dangerouslySetInnerHTML={{ __html: procedureCategoryIcon }}
        />
      )}

      {/* if hidden procedure give this style to input style={{ color: 'var(--color-neutral-contrast-05)' }}  */}
      <Tooltip
        arrow={true}
        open={tooltipOpen}
        position="top"
        html={
          <div style={{ maxWidth: '200px' }}>
            Special characters not allowed in file name (+, #, &#x7b;, &#x7d;,
            `, %, &amp;, /, *, ^, $, &#39;, ;, @, =, |, ~, !, [, ], &quot;,
            &#44;)
          </div>
        }
        style={{ flex: 1 }}
        className={`${
          isFirstTimeProcedure ? 'procedure__tooltip__wrapper' : ''
        }`}
      >
        <input
          type="text"
          className="procedure__name"
          ref={procedureNameInputRef}
          value={newName}
          title={procedureItem?.ProcedureName}
          style={{
            color:
              visibility === false ? 'var(--color-neutral-contrast-05)' : '',
          }}
          readOnly={!editMode}
          onClick={
            editMode
              ? undefined
              : () => {
                  setRenameProc(undefined);
                  selectProcedure(false, procedureItem);
                }
          }
          onChange={(e: SyntheticEvent<HTMLInputElement>) => {
            setNewName(e.currentTarget.value);
            const format = /[*|"[\]{}';@&$+#=~,`%^!/]/;
            if (format.test(e.currentTarget.value)) {
              setTooltipOpen(true);
            } else {
              setTooltipOpen(false);
            }
          }}
        />
      </Tooltip>

      {renameProc !== undefined && (
        <Stack direction="x" gap={2} className="procedure__name-edit">
          <button
            disabled={tooltipOpen}
            title="Rename"
            onClick={() => {
              const trimmed = newName?.trim() ?? '';
              if (
                trimmed === undefined ||
                trimmed.trim() === '' ||
                trimmed === '.html'
              ) {
                showErrorToast({
                  message: 'Procedure should not be empty',
                });
                return;
              }

              const isValid =
                !trimmed.includes('.html') || /[^.\s]+\.html/.test(trimmed);

              if (!isValid) {
                setNewName(procedureNameRef.current);
                setTooltipOpen(false);
                setRenameProc(undefined);
                return;
              }
              setRenameFailed(false);

              let newContent = '';
              if (
                (window as any)?.tinymce?.activeEditor?.initialized === true
              ) {
                newContent = `<!DOCTYPE html><html><head><title></title><base href='/api/procedures/V1/accountfolders/${
                  procedureDetails?.AccountProcedure?.AccountId
                }/Policies/'></head><body>${(
                  window as any
                )?.tinymce?.activeEditor?.getContent()}</body></html>`;
              }
              if (
                (window as any)?.tinymce?.activeEditor?.isDirty() &&
                newContent !== procedureDetails?.Content &&
                editorChanges
              )
                autoSaveContent(() => {
                  setUserTyped(false);
                  setEditorChanges(false);
                });

              updateName(trimmed, (isSuccess: any) => {
                if (!isSuccess) {
                  setNewName(procedureNameRef.current);
                }
              });
              setRenameProc();

              setIsNameEdited(false);
              if (!trimmed.endsWith('.html')) {
                if (!procedureItem?.ProcedureName.endsWith('.htm')) {
                  const extension = `${trimmed}.html`;
                  setNewName(trimmed.replace(trimmed, extension));
                } else {
                  const extension = `${trimmed}.html`;
                  setNewName(trimmed.replace(trimmed, extension));
                }
              }
            }}
          >
            <Icon.Check title="Rename" />
          </button>
          <button
            title="Cancel"
            onClick={() => {
              setTooltipOpen(false);
              setIsNameEdited(false);
              setRenameProc();
              setNewName(procedureItem?.ProcedureName);
            }}
          >
            <Icon.X title="Cancel" />
          </button>
        </Stack>
      )}

      {isFirstTimeProcedure && (
        <Tooltip
          animation="fade"
          title="Publish to make available to Proteus Call Center"
          position="top"
          arrow={true}
          className="procedure__flag__tooltip"
        >
          <span className="procedure__flag">Draft</span>
        </Tooltip>
      )}

      <Stack direction="x" gap={2} className="procedure__attributes">
        <Icon.LinkedProcedure
          fill={
            procedureItem?.IsLinked === true
              ? 'var(--color-state-info)'
              : procedureItem?.HasLinkedChilds === true
              ? 'var(--color-state-success)'
              : 'var(--color-neutral-contrast-03)'
          }
        />
        <Icon.Globe
          fill={
            procedureItem?.IsGlobal === true
              ? 'var(--color-state-success)'
              : 'var(--color-neutral-contrast-03)'
          }
        />
        <ProcedureBrokenLinks count={procedureItem?.BrokenLinksCount} />
      </Stack>

      <Stack direction="x" gap={2} className="procedure__actions">
        {/* procedure is a global procedure and show the linked accounts to that procedure in a panel */}
        <button
          className={
            procedureItem?.IsLinked === true ||
            procedureItem?.HasLinkedChilds === true
              ? ''
              : 'hidden'
          }
          onClick={toggleLinkedAccounts}
        >
          <Icon.LinkedProcedure
            title={
              procedureItem?.IsLinked ? 'Linked (child)' : 'Linked (parent)'
            }
          />{' '}
        </button>
        <button
          className={procedureItem?.IsGlobal === true ? '' : 'hidden'}
          onClick={toggleLinkedGlobalAccounts}
        >
          <Icon.Globe title="View linked accounts" />
        </button>

        <button
          onClick={() => {
            if (!isCheckedAllProcedures()) {
              selectProcedure(false, procedureItem, true);
            }
            setRenameProc({
              AccountId: procedureItem.AccountId,
              ProcedureId: procedureItem.ProcedureId,
              ProcedureName: procedureItem.ProcedureName,
            });
            setIsNameEdited(true);
            setEditedProcedure(procedureItem);
          }}
        >
          <Icon.Edit title="Rename procedure" />
        </button>
      </Stack>
    </ProcedureListItem>
  );
}
